import React from "react";
import { FigureRow, H, Note, P, Photo, Term, UL } from "./elements";

export const MillingArticle: React.FC = () => (
  <>
    <P>
      Lumber comes in three states of readiness. <Term>S4S</Term> (surfaced four
      sides) is flat, straight, and ready to use. <Term>S2S</Term> has surfaced
      faces but rough edges. <Term>Rough sawn</Term> lumber is neither flat nor
      straight. Rough stock is cheaper because the surfacing work, called{" "}
      <Term>milling</Term>, is left to you. The hardware store only sells
      surfaced wood; S2S and rough stock hang in the racks at the lumberyard.
    </P>
    <FigureRow>
      <Photo src="/images/benchtop-jointer.png" caption="jointer — flattens" />
      <Photo src="/images/lunchbox-planer.png" caption="planer — thicknesses" />
      <Photo src="/images/jobsite-table-saw.png" caption="table saw — rips" />
    </FigureRow>

    <H>Faces and Edges</H>
    <P>
      A board has <Term>faces</Term> (which need to be flat, then parallel) and{" "}
      <Term>edges</Term> (straight, then parallel). Each is its own job:
    </P>
    <UL>
      <li>
        <Term>Flatten a face</Term> — the jointer, or a hand plane at any bench.
        One flat face becomes the reference for everything else.
      </li>
      <li>
        <Term>Make the faces parallel</Term> — the planer, with the flat
        reference face against its bed.
      </li>
      <li>
        <Term>Straighten an edge</Term> — the jointer (after flattening a face,
        since the fence registers against it), the straight-line sled on the
        table saw (the board rides the sled, so a rough edge is fine), or the
        hand plane.
      </li>
      <li>
        <Term>Rip to width</Term> — the table saw, once one edge is straight
        enough to run against the fence. The band saw rips too, and its fence
        takes a rough edge; the blade leaves the fresh edge rough, so plan on
        jointing it.
      </li>
    </UL>
    <P>
      Once a board has a flat face and a straight edge, the remaining steps can
      happen in any order. Crosscuts to length can happen at any point.
    </P>

    <H>Room to Run Stock</H>
    <P>
      A board starts entirely on the infeed side of a machine and ends entirely
      past the cutter. The jointer, planer, table saw, and band saw all need
      clear floor at both ends of the cut, scaled to the stock: an 8' rip needs
      about 7' clear on each side of the table saw. If there isn't room, the
      machine shows which side is short. Stock too long for the room it's in
      gets crosscut to length at the miter saw first; a chop saw holds the board
      still, so it works anywhere.
    </P>
    <Note>
      Stock slides right over a bare worktable. Park one past the saw and you've
      got yourself an outfeed table.
    </Note>

    <H>Running the Planer</H>
    <P>
      Set the cut height with the crank, then feed stock in from your hands.
      Each pass removes <Term>one detent</Term> of thickness: stock one detent
      above the setting comes out at the setting, and stock already at the
      setting gets a light skim pass that surfaces it without changing its size.
      Thicknessing 8/4 stock down to 4/4 takes four passes, cranking the height
      down between each. The feed rollers carry the board through on their own,
      so you're free to do other work while a pass finishes.
    </P>

    <H>Resawing</H>
    <P>
      <Term>Resawing</Term> splits a board through its thickness: one 8/4 blank
      yields two 4/4 boards. The saw's fence is set in <Term>quarters</Term>;
      the distance from the blade is the thickness of the piece it takes off.
      Where a saw both rips and resaws, press <Term>R</Term> at it to turn the
      stock between lying flat and standing on edge.
    </P>
    <UL>
      <li>
        <Term>Band saw</Term> — any width the shop stocks. The blade is thin
        enough that the two halves add up to the board you started with. The
        fence rides whatever the board has, rough stock included, and the pieces
        come away referenced the same as the board went in. Both fresh faces
        come off rough, so plan on planing or sanding them.
      </li>
      <li>
        <Term>Table saw</Term> — up to 6" wide, since the cut is two passes that
        meet in the middle. The blade takes a quarter inch, but leaves cleaner
        faces. The board needs a straight edge as well as a flat face to stand
        up square against the fence. Press R to lay the stock back down and rip.
      </li>
    </UL>

    <H>Surface Quality</H>
    <P>
      Besides its shape, every board and panel has a surface quality: rough →
      smooth → sanded. Planing and jointing leave a smooth surface; only sanding
      produces a sanded one. Glued-up panels always come out of the clamps
      rough, so plan on re-surfacing after every glue-up.
    </P>
    <P>
      Surface decides what a piece can become, not what it fetches. Rustic work
      takes wood in any condition; frames, shelves, boxes, and cutting boards
      all call for sanded stock and won't go together without it.
    </P>

    <Note>
      Never feed end grain through the planer — it will tear the panel apart.
      Sand it flat instead.
    </Note>
  </>
);
